if (!customElements.get('ask-question-form')) {
  customElements.define(
    'ask-question-form',
    class AskQuestionForm extends HTMLFormElement {
      constructor() {
        super();
        this.emailInput = this.querySelector('#AskQuestionForm-Email');
        this.emailError = this.querySelector('#AskQuestionForm-Email-error');
        this.submitButton = this.querySelector('.btn-submit');
        
        if (this.emailInput) {
          this.emailInput.addEventListener('blur', this.validateEmail.bind(this));
          this.emailInput.addEventListener('input', this.clearError.bind(this));
        }
        
        this.addEventListener('submit', this.handleSubmit.bind(this));
      }

      /**
       * Validates email format and checks for spam patterns
       * @param {string} email - Email address to validate
       * @returns {Object} - { valid: boolean, message: string }
       */
      validateEmailFormat(email) {
        // More restrictive email regex pattern
        // Requires: valid local part, @ symbol, valid domain with TLD
        const emailRegex = /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9.-]*[a-zA-Z0-9])?\.[a-zA-Z]{2,}$/;
        
        if (!email || email.trim() === '') {
          return { valid: false, message: 'Email is required' };
        }

        const trimmedEmail = email.trim();

        // Check for repetitive characters (more than 3 consecutive same characters)
        const repetitivePattern = /(.)\1{3,}/;
        if (repetitivePattern.test(trimmedEmail)) {
          return { valid: false, message: 'Email contains too many repetitive characters' };
        }

        // Check for very long strings of numbers (more than 6 consecutive digits) - spam prevention
        const longNumbersPattern = /\d{7,}/;
        if (longNumbersPattern.test(trimmedEmail)) {
          return { valid: false, message: 'Email contains invalid number sequences' };
        }

        // Check for multiple consecutive dots
        const multipleDotsPattern = /\.{2,}/;
        if (multipleDotsPattern.test(trimmedEmail)) {
          return { valid: false, message: 'Email format is invalid' };
        }

        // Check for invalid characters
        const invalidCharsPattern = /[^a-zA-Z0-9._@-]/;
        if (invalidCharsPattern.test(trimmedEmail)) {
          return { valid: false, message: 'Email contains invalid characters' };
        }

        // Check email length (reasonable limits)
        if (trimmedEmail.length > 254) {
          return { valid: false, message: 'Email address is too long' };
        }

        // Split email into local and domain parts
        const parts = trimmedEmail.split('@');
        if (parts.length !== 2) {
          return { valid: false, message: 'Email format is invalid' };
        }

        const [localPart, domainPart] = parts;

        // Check for excessive numbers in local part (more than 50% numbers) - spam indicator
        const numberCount = (localPart.match(/\d/g) || []).length;
        if (numberCount > localPart.length * 0.5 && localPart.length > 5) {
          return { valid: false, message: 'Email format appears invalid' };
        }

        // Validate local part (before @)
        if (localPart.length < 1 || localPart.length > 64) {
          return { valid: false, message: 'Email local part is invalid' };
        }

        // Validate domain part (after @)
        if (domainPart.length < 4 || domainPart.length > 253) {
          return { valid: false, message: 'Email domain is invalid' };
        }

        // Check domain has at least one dot
        if (!domainPart.includes('.')) {
          return { valid: false, message: 'Email domain format is invalid' };
        }

        // Check domain doesn't start or end with dot or hyphen
        if (domainPart.startsWith('.') || domainPart.endsWith('.') || 
            domainPart.startsWith('-') || domainPart.endsWith('-')) {
          return { valid: false, message: 'Email domain format is invalid' };
        }

        // Final regex validation
        if (!emailRegex.test(trimmedEmail)) {
          return { valid: false, message: 'Please enter a valid email address' };
        }

        return { valid: true, message: '' };
      }

      validateEmail(event) {
        const email = this.emailInput.value.trim();
        const validation = this.validateEmailFormat(email);

        if (!validation.valid) {
          this.showError(validation.message);
          this.emailInput.setAttribute('aria-invalid', 'true');
          return false;
        } else {
          this.clearError();
          this.emailInput.removeAttribute('aria-invalid');
          return true;
        }
      }

      showError(message) {
        if (this.emailError) {
          this.emailError.textContent = message;
          this.emailError.classList.remove('hidden');
        }
        if (this.emailInput) {
          this.emailInput.classList.add('form-control--error');
        }
      }

      clearError() {
        if (this.emailError) {
          this.emailError.textContent = '';
          this.emailError.classList.add('hidden');
        }
        if (this.emailInput) {
          this.emailInput.classList.remove('form-control--error');
        }
      }

      handleSubmit(event) {
        const email = this.emailInput.value.trim();
        const validation = this.validateEmailFormat(email);

        if (!validation.valid) {
          event.preventDefault();
          this.showError(validation.message);
          this.emailInput.setAttribute('aria-invalid', 'true');
          this.emailInput.focus();
          return false;
        }

        return true;
      }
    },
    { extends: 'form' }
  );
}
